import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionHeaders } from "@/lib/session";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable: string;
}

interface SyncingJob {
  id: string;
  connection_id: string;
  job_name: string;
  connection_name: string;
  source_table_name: string;
  dest_table_name: string | null;
  columns: unknown;
  offset_column: string | null;
  client_id?: string;
}

interface EditSyncJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: SyncingJob | null;
  onSaved: () => void;
}

const EditSyncJobDialog = ({ open, onOpenChange, job, onSaved }: EditSyncJobDialogProps) => {
  const [schemaColumns, setSchemaColumns] = useState<ColumnInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCols, setSelectedCols] = useState<string[]>([]);
  const [offsetColumn, setOffsetColumn] = useState<string | null>(null);
  const [colDescriptions, setColDescriptions] = useState<Record<string, string>>({});
  const [destTableName, setDestTableName] = useState<string>("");

  useEffect(() => {
    if (!open || !job) return;
    // Init from job
    const cols = Array.isArray(job.columns)
      ? (job.columns as any[]).map((c: any) => c.column_name || c)
      : [];
    setSelectedCols(cols);
    setOffsetColumn(job.offset_column);
    setDestTableName(job.dest_table_name || "");
    // Load saved descriptions
    const descs: Record<string, string> = {};
    if (Array.isArray(job.columns)) {
      (job.columns as any[]).forEach((c: any) => {
        if (c.description) descs[c.column_name || c] = c.description;
      });
    }
    setColDescriptions(descs);
    fetchSchema();
  }, [open, job?.id]);

  const fetchSchema = async () => {
    if (!job) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("db-schema", {
        headers: await getSessionHeaders(),
        body: { connection_id: job.connection_id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const tableSchema = (data.schema || []).find(
        (t: any) => t.table_name === job.source_table_name
      );
      const cols = tableSchema?.columns || [];
      setSchemaColumns(cols);
      // Auto-default offset column if not set: updated_at > created_at > id
      if (!job.offset_column && cols.length > 0) {
        const updatedAt = cols.find((c: ColumnInfo) => c.column_name === "updated_at" || c.column_name === "update_at");
        const createdAt = cols.find((c: ColumnInfo) => c.column_name === "created_at");
        const idCol = cols.find((c: ColumnInfo) => c.column_name === "id");
        const autoOffset = updatedAt || createdAt || idCol;
        if (autoOffset) setOffsetColumn(autoOffset.column_name);
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to fetch schema");
    } finally {
      setLoading(false);
    }
  };

  const toggleCol = (colName: string) => {
    setSelectedCols((prev) =>
      prev.includes(colName) ? prev.filter((c) => c !== colName) : [...prev, colName]
    );
  };

  const handleSave = async () => {
    if (!job) return;
    if (selectedCols.length === 0) {
      toast.error("Select at least one column");
      return;
    }
    if (!offsetColumn) {
      toast.error("Please select an offset column");
      return;
    }
    if (!selectedCols.includes(offsetColumn)) {
      toast.error("Offset column must be among selected columns");
      return;
    }
    setSaving(true);
    try {
      const columnsJson = selectedCols.map((colName) => {
        const colInfo = schemaColumns.find((c) => c.column_name === colName);
        return {
          column_name: colName,
          data_type: colInfo?.data_type || "text",
          is_nullable: colInfo?.is_nullable || "YES",
          ...(colDescriptions[colName] ? { description: colDescriptions[colName] } : {}),
        };
      });
      const { error } = await supabase
        .from("pipeline_jobs" as any)
        .update({
          columns: columnsJson,
          offset_column: offsetColumn,
          dest_table_name: destTableName.trim() || null,
        } as any)
        .eq("id", job.id);
      if (error) throw error;
      toast.success("Sync job updated");
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            Edit Sync — {job?.source_table_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="dest-table-name" className="text-sm font-medium">Destination Table Name</Label>
          <Input
            id="dest-table-name"
            placeholder="Enter destination table name…"
            value={destTableName}
            onChange={(e) => setDestTableName(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Fetching columns…
          </div>
        ) : schemaColumns.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No columns found.</p>
        ) : (
          <div className="space-y-1">
            {schemaColumns.map((col) => {
              const checked = selectedCols.includes(col.column_name);
              const isOffset = offsetColumn === col.column_name;
              return (
                <div
                  key={col.column_name}
                  className="rounded px-2 py-1.5 text-sm hover:bg-accent/30 transition-colors space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleCol(col.column_name)}
                    />
                    <span className="flex-1">{col.column_name}</span>
                    <span className="text-xs text-muted-foreground">{col.data_type}</span>
                    <button
                      onClick={() =>
                        setOffsetColumn((prev) =>
                          prev === col.column_name ? null : col.column_name
                        )
                      }
                      className={cn(
                        "text-[10px] px-1.5 py-0.5 rounded border transition-colors shrink-0",
                        isOffset
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      )}
                    >
                      {isOffset ? "Offset ✓" : "Offset"}
                    </button>
                  </div>
                  <div className="ml-6">
                    <Input
                      placeholder="Add column description..."
                      className="h-7 text-xs"
                      value={colDescriptions[col.column_name] || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setColDescriptions((prev) => ({ ...prev, [col.column_name]: val }));
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditSyncJobDialog;
